import React from 'react';
import PropTypes from 'prop-types';
import { Button, GapChip, Tooltip } from '../ui';
import { formatCurrency, formatNumber, getBuyerHeatStyle, verdictLabel } from '../../utils/formatting';
import { aiPriceState, consistentAiRange } from '../../utils/aiPrice';
import { ANOMALY_GROUPS, anomalyReasonText } from '../../utils/anomalySignals';
import { daysToMedianSale } from '../../utils/riskMeter';
import { aiPriceTip } from '../InventoryDashboard/AiPriceCell';
import { unitAgingLabel, unitAgingTone } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { RiskPill, TRUE_COST_TIP } from '../InventoryV2/InventoryTableV2';
import { unitDisplayName, unitYear } from '../../utils/inventoryView';
import { NOT_FOR_COMMITTED, dealHeadline } from '../../utils/committedUnit';
import { CopyIcon, ExternalIcon, PhotoIcon, PlusIcon, BoltIcon } from './icons';

const SEVERITY_TONE = { critical: 'crit', warning: 'warn', positive: 'pos' };
const HEAT_LEVEL = { Hot: 3, Warm: 2, Cold: 1, 'No Interest': 0 };
const TYPE_LABEL = { N: 'New', U: 'Used' };

/** The anomaly group a verdict belongs to ("Watch List", …), or null. */
export const verdictGroup = (verdict) => ANOMALY_GROUPS.find((g) => g.verdicts.includes(verdict))?.label || null;

/**
 * The hero's signal chips (U4), each a button that jumps to the tab with the
 * detail: the anomaly verdict (Intelligence), a live quote at or above the AI
 * price (Pipeline), buyer interest (Pipeline) and open tasks (Activity).
 * Built from today's fields only.
 */
// `liveQuote`: the highest OPEN quote from the pipeline (the margin
// calculator's source) once loaded; null when loaded with none. Undefined
// before the pipeline arrives: the chip then shows no amount, never the
// inventory's MaxOpenQuoteTotal, which also counts expired / No Sale quotes
// and would flash a wrong figure (QA BUG-4 and re-test).
export function heroSignals(unit, { openTasks = 0, liveQuote } = {}) {
    const out = [];
    if (unit.AnomalyVerdict && unit.AnomalySeverity && unit.AnomalySeverity !== 'normal' && SEVERITY_TONE[unit.AnomalySeverity]) {
        const group = verdictGroup(unit.AnomalyVerdict);
        out.push({
            id: 'anomaly',
            tab: 'ai',
            tone: SEVERITY_TONE[unit.AnomalySeverity],
            text: verdictLabel(unit.AnomalyVerdict),
            title: `${group ? `${group} signal: ` : ''}${anomalyReasonText(unit) || unit.AnomalyVerdict}`,
        });
    }
    if (unit.HasQualifyingQuote) {
        const amount = liveQuote;
        out.push({
            id: 'livequote',
            tab: 'pipeline',
            tone: 'opp',
            icon: 'bolt',
            text: amount > 0
                ? `Live quote ${formatCurrency(amount)} ≥ AI price`
                : 'Live quote ≥ AI price',
            title: 'Open quote at or above the AI price: call the buyer before changing the price',
        });
    }
    if (unit.QuoteCount > 0) {
        const heat = getBuyerHeatStyle(unit.QuoteCount, unit.DaysSinceLastQuote);
        out.push({
            id: 'heat',
            tab: 'pipeline',
            tone: 'plain',
            level: HEAT_LEVEL[heat.label] ?? 0,
            text: `${heat.label} · ${unit.QuoteCount} quote${unit.QuoteCount === 1 ? '' : 's'}`,
            title: unit.DaysSinceLastQuote != null ? `Last quoted ${unit.DaysSinceLastQuote}d ago` : 'Never quoted',
        });
    }
    if (openTasks > 0) {
        out.push({
            id: 'tasks', tab: 'activity', tone: 'plain', dot: 'grey', text: `${openTasks} open task${openTasks === 1 ? '' : 's'}`,
            title: 'Open and in-progress tasks on this unit',
        });
    }
    return out;
}

function Key({ label, children, sub, info }) {
    return (
        <div className="u2-key">
            <span className="u2-key-l">{label}{info}</span>
            <span className="u2-key-n">{children}</span>
            {sub && <span className="u2-key-s">{sub}</span>}
        </div>
    );
}
Key.propTypes = { label: PropTypes.node, children: PropTypes.node, sub: PropTypes.node, info: PropTypes.node };

/**
 * The unit page hero (U4, decision D8): photo, identity, the four key
 * numbers, the signal chips and the quick actions. The Pending variant keeps
 * today's wording (P-UNIT-008, P-UNIT-038): "Pending" status and "Cost
 * estimate" instead of true cost.
 *
 * A committed unit (`committed`: sale pending, pre-sold, sold) shows its deal
 * status as the status chip, with its date ("Sale pending · marked sold
 * Sep 22"), instead of In stock / Pending; "Not yet received" instead of a
 * days figure; and no sell-speed risk. Its in-stock-only fields arrive blank
 * (utils/committedUnit), so no anomaly or live-quote chip appears either.
 *
 * An on-hold unit (in stock, held in Salesforce) shows an "On hold" chip, its
 * reason as the tooltip, next to In stock / Pending; nothing else changes.
 */
export default function UnitHero({
    unit, committed = false, machineCatalog, images, imagesLoading, openTasks, liveQuote, listingUrl, readOnly,
    onGoTab, onOpenPhoto, onCreateTask, onCopyLink, titleRef,
}) {
    const { name, original } = unitDisplayName(unit, machineCatalog);
    const pending = unit.StockStatus === 'P';
    const { aging, gapThresholds } = useDealershipSettings();
    const ageTone = unitAgingTone(unit, aging);
    const ageLabel = unitAgingLabel(unit, aging);
    const aiState = aiPriceState(unit, gapThresholds);
    const hasAi = typeof unit.PredictedPrice === 'number' && unit.PredictedPrice > 0;
    const range = consistentAiRange(unit);
    const median = daysToMedianSale(unit);
    const signals = heroSignals(unit, { openTasks, liveQuote });
    const photoCount = images.length;
    const meters = [
        unit.ODOMETER != null ? `${formatNumber(unit.ODOMETER)} h` : null,
        unit.Sep_Meter > 0 ? `${formatNumber(unit.Sep_Meter)} separator h` : null,
    ].filter(Boolean).join(' · ');
    const branch = unit.BranchName ?? unit.LOCATION;
    const deal = dealHeadline(unit);

    return (
        <section className="u2-hero" aria-labelledby="u2-title">
            {photoCount > 0 ? (
                <button
                    type="button"
                    className="u2-photo"
                    onClick={() => onOpenPhoto(0)}
                    aria-label={`Open photos, ${photoCount} image${photoCount === 1 ? '' : 's'}`}
                >
                    <img src={images[0]} alt="" />
                    <span className="u2-photo-pc"><PhotoIcon />{photoCount} photo{photoCount === 1 ? '' : 's'}</span>
                </button>
            ) : (
                <div className="u2-photo u2-photo--empty" aria-hidden={imagesLoading ? 'true' : undefined}>
                    <span className="u2-photo-pc">
                        <PhotoIcon />
                        {imagesLoading ? 'Loading photos…' : 'No photos'}
                    </span>
                </div>
            )}

            <div className="u2-h-body">
                <div className="u2-h-top">
                    <span className="u2-stock">#{unit.StockNumber}</span>
                    {deal && (
                        <Tooltip content={deal.tip}>
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                            <span className={`u2-status u2-status--deal is-${deal.status}`} tabIndex={0}>{deal.text}</span>
                        </Tooltip>
                    )}
                    {!committed && pending && <span className="u2-status u2-status--pend">Pending</span>}
                    {!committed && unit.StockStatus === 'S' && <span className="u2-status u2-status--in">In stock</span>}
                    {TYPE_LABEL[unit.NewUsedStatus] && (
                        <span className="u2-tag" title={`${TYPE_LABEL[unit.NewUsedStatus]} equipment`}>{TYPE_LABEL[unit.NewUsedStatus]}</span>
                    )}
                    {unit.DaysInStock != null && (
                        <span className="u2-h-days">
                            <i className={`inv2-age v2-tone-${ageTone}`} aria-hidden="true" />
                            {' '}<b>{formatNumber(unit.DaysInStock)} {Number(unit.DaysInStock) === 1 ? 'day' : 'days'}</b>
                            {ageLabel ? ` · ${ageLabel} bucket` : ''}
                        </span>
                    )}
                    {committed && unit.DaysInStock == null && <span className="u2-h-days">Not yet received</span>}
                </div>
                <div className="u2-h-title">
                    <h1 id="u2-title" ref={titleRef} tabIndex={-1} title={original !== name ? original : undefined}>{name}</h1>
                    {unitYear(unit) && <span className="u2-yr">{unitYear(unit)}</span>}
                </div>
                <div className="u2-h-sub">
                    {unit.CATEGORY && <span><span className="u2-cat">{unit.CATEGORY}</span></span>}
                    {branch && <span>{branch} branch</span>}
                    <span>{unit.Salesman ? `Salesman ${unit.Salesman}` : 'Unassigned'}</span>
                    {meters && <span className="u2-num">{meters}</span>}
                </div>

                <div className="u2-keys">
                    <Key label="Asking" sub="list price">
                        {unit.Best_Sell_Price > 0 ? formatCurrency(unit.Best_Sell_Price) : '—'}
                    </Key>
                    <Key
                        label={<><span className="u2-spark-dot" aria-hidden="true" />AI price</>}
                        info={<Tooltip label="How the AI price chip works" content={aiPriceTip(gapThresholds)} />}
                        sub={range ? <>likely range <b>{formatCurrency(range.low)}–{formatCurrency(range.high)}</b></> : null}
                    >
                        {hasAi ? formatCurrency(unit.PredictedPrice) : '—'}
                        {aiState !== 'none' && <GapChip unit={unit} />}
                    </Key>
                    <Key
                        label={pending ? 'Cost estimate' : 'True cost'}
                        info={<Tooltip label={pending ? 'What the cost estimate is' : 'What true cost is'} content={TRUE_COST_TIP} />}
                        sub={unit.GrossMargin != null
                            ? <>gross margin <b>{formatCurrency(unit.GrossMargin)}</b>{unit.GrossMarginPct != null ? ` · ${unit.GrossMarginPct.toFixed(1)}%` : ''}</>
                            : null}
                    >
                        {unit.CarryingCostBasis != null ? formatCurrency(unit.CarryingCostBasis) : '—'}
                    </Key>
                    {committed ? (
                        <Key label="Sell-speed risk">
                            <span className="u2-muted u2-key-none">{NOT_FOR_COMMITTED}</span>
                        </Key>
                    ) : (
                        <Key
                            label="Sell-speed risk"
                            sub={median != null ? `median ~${median} more days to sell` : null}
                        >
                            {unit.RiskTier ? <RiskPill unit={unit} /> : <span className="u2-muted u2-key-none">Not scored</span>}
                        </Key>
                    )}
                </div>

                <div className="u2-h-row">
                    {signals.length > 0 && (
                        <div className="u2-sigs" role="group" aria-label="Signals on this unit">
                            {signals.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    className={`u2-sig u2-sig--${s.tone}`}
                                    title={s.title}
                                    onClick={() => onGoTab(s.tab)}
                                >
                                    {s.icon === 'bolt' && <BoltIcon />}
                                    {s.dot && <i className={`v2-tone-${s.dot}`} aria-hidden="true" />}
                                    {s.level !== undefined && (
                                        <span className="inv2-bars" aria-hidden="true">
                                            {[1, 2, 3].map((i) => <span key={i} className={i <= s.level ? 'on' : undefined} />)}
                                        </span>
                                    )}
                                    {s.tone !== 'plain' && !s.icon && <i className="u2-sig-dot" aria-hidden="true" />}
                                    {s.text}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="u2-acts">
                        {!readOnly && (
                            <Button variant="primary" onClick={onCreateTask}><PlusIcon />Create task</Button>
                        )}
                        <Button onClick={onCopyLink}><CopyIcon />Copy link</Button>
                        {listingUrl && (
                            <a
                                className="v2-btn v2-btn--default"
                                href={listingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Opens the public marketplace listing in a new tab"
                            >
                                <ExternalIcon />Open listing
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

UnitHero.propTypes = {
    unit: PropTypes.object.isRequired,
    committed: PropTypes.bool,
    machineCatalog: PropTypes.object,
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    imagesLoading: PropTypes.bool,
    openTasks: PropTypes.number,
    liveQuote: PropTypes.number,
    listingUrl: PropTypes.string,
    readOnly: PropTypes.bool,
    onGoTab: PropTypes.func.isRequired,
    onOpenPhoto: PropTypes.func.isRequired,
    onCreateTask: PropTypes.func.isRequired,
    onCopyLink: PropTypes.func.isRequired,
    titleRef: PropTypes.object,
};
